import {
  Space,
  Table,
  Button,
  notification,
  Modal,
  Input,
  Tag,
} from "antd";
import type { ColumnsType } from "antd/lib/table";
import { SearchOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import styles from "./toolsIA.module.css";
import { FC, useEffect, useState } from "react";
import { useToolsIA } from "../../hooks/useToolsIA";
import { getToolsIA_getToolsIA } from "../../__generated__/gql-types/getToolsIA";

const { Search } = Input;

const ToolsIA: FC = () => {
  const userLanguage = navigator.language.split('-')[0]; // Obtenemos el idioma del navegador (es o en)
  const { toolsIA, loading: isLoading, deleteToolsIA, refetch } = useToolsIA();
  const [dataSource, setdataSource] = useState<getToolsIA_getToolsIA[] | null>([]);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [idLiteracy, setIdLiteracy] = useState<string>("");

  // Función para determinar qué idiomas están disponibles
  const getAvailableLanguages = (record: getToolsIA_getToolsIA) => {
    const languages: string[] = [];
    
    // Verifica si hay contenido en español (al menos título)
    if (record.title || record.description || record.prompt) {
      languages.push('es');
    }
    
    // Verifica si hay contenido en inglés (al menos título)
    if (record.title_en || record.description_en || record.prompt_en) {
      languages.push('en');
    }
    
    return languages;
  };

  useEffect(() => {
    setdataSource(toolsIA);
  }, [toolsIA]);



  const showModal = (id: string) => {
    setIsModalVisible(true);
    setIdLiteracy(id);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleOk = async () => {
    try {
      await deleteToolsIA(idLiteracy);
      setIsModalVisible(false);
    } catch {
      notification["error"]({
        message: "Eliminación Erronea",
        description: "La Herramienta IA no pudo ser eliminada",
        placement: "top",
        duration: 3,
      });
    }
  };

  const columns: ColumnsType<getToolsIA_getToolsIA> = [
    {
      align: "center",
      title: "ID",
      dataIndex: "id",
      key: "id",
    },
    { 
      align: "center", 
      title: "Título", 
      dataIndex: "title", 
      key: "title",
      render: (text: string | undefined, record: getToolsIA_getToolsIA) => {
        const availableLanguages = getAvailableLanguages(record);
        const displayTitle = userLanguage === 'es' 
          ? record.title || record.title_en || 'Sin título'
          : record.title_en || record.title || 'Sin título';
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <span>{displayTitle}</span>
                         {availableLanguages.length === 2 && (
               <Tag color="purple" style={{ margin: 0, fontSize: '10px', padding: '2px 6px', height: 'auto' }}>
                 BILINGÜE
               </Tag>
             )}
          </div>
        );
      }
    },
    { 
      align: "center", 
      title: "Idiomas Disponibles", 
      dataIndex: "languages", 
      key: "languages",
      render: (text: string | undefined, record: getToolsIA_getToolsIA) => {
        const availableLanguages = getAvailableLanguages(record);
        
        return (
          <Space>
            {availableLanguages.includes('es') && (
              <Tag color="blue" style={{ fontWeight: 'bold' }}>
                🇪🇸 Español
              </Tag>
            )}
            {availableLanguages.includes('en') && (
              <Tag color="green" style={{ fontWeight: 'bold' }}>
                🇺🇸 English
              </Tag>
            )}
            {availableLanguages.length === 0 && (
              <Tag color="red">
                ⚠️ Sin contenido
              </Tag>
            )}
          </Space>
        );
      }
    },
    {
      align: "center",
      title: "Acciones",
      key: "acciones",
      render: (_, record) => {
        return (
          <Space size={15} style={{ fontSize: 19 }}>
            <Link to={`/toolsia/${record.id}`} state={record.id}>
              <Button icon={<SearchOutlined />}>Editar</Button>
            </Link>
            <Button
              onClick={() => {
                record && record.id && showModal(record.id);
              }}
              danger
            >
              Eliminar
            </Button>
          </Space>
        );
      },
    },
  ];

  const onSearch = (value: string) => {
    if (value === "") {
      setdataSource(toolsIA);
    } else {
      if (toolsIA) {
        const searchValue = value.toLowerCase();
        const filteredData: getToolsIA_getToolsIA[] = toolsIA.filter(
          (data) => 
            // Buscar en títulos (español e inglés)
            data.title?.toLowerCase().includes(searchValue) ||
            data.title_en?.toLowerCase().includes(searchValue) ||
            // Buscar en descripciones (español e inglés)
            data.description?.toLowerCase().includes(searchValue) ||
            data.description_en?.toLowerCase().includes(searchValue) ||
            // Buscar en prompts (español e inglés)
            data.prompt?.toLowerCase().includes(searchValue) ||
            data.prompt_en?.toLowerCase().includes(searchValue)
        );
        setdataSource(filteredData);
      }
    }
  };

  const onSearchId = (value: string) => {
    if (value === "") {
      setdataSource(toolsIA);
    } else {
      if (toolsIA) {
        const filteredData: getToolsIA_getToolsIA[] = toolsIA.filter(
          (data) => data.id === value
        );
        setdataSource(filteredData);
      }
    }
  };

  return (
    <div className={styles.container}>
      <h1>Herramientas IA</h1>
      <div className={styles.newQuizzesContainer}>
        <Space direction="vertical" style={{ marginRight: 20 }}>
          <Search
            placeholder="Buscar por id"
            onSearch={onSearchId}
            enterButton
          />
        </Space>
        <Space direction="vertical" style={{ marginRight: 20 }}>
          <Search
            placeholder="Buscar por título, descripción o prompt (ES/EN)"
            onSearch={onSearch}
            enterButton
            style={{ minWidth: 300 }}
          />
        </Space>
        <Button type="primary" danger>
          <Link to="/toolsia/new">Nueva Herramienta IA</Link>
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={dataSource ?? []}
        loading={isLoading}
        rowKey={(record) => (record && record.id ? record.id : "")}
      />
      <Modal
        title="Eliminar Heerramienta IA"
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <p>¿Desea eliminar la herramienta IA?</p>
      </Modal>
    </div>
  );
};

export default ToolsIA;
